<template>
  <div class="guide-enterprise-sso">

    <h2 id="overview">Enterprise SSO and SCIM at a glance</h2>
    <p>
      eSign Launchpad supports three identity-protocol integrations for organizations that
      run their workforce off a central identity provider (Okta, Microsoft Entra ID, JumpCloud,
      Google Workspace, Auth0, Ping, others). Pick what fits your stack:
    </p>

    <ul>
      <li><strong>SAML 2.0</strong> for browser-based single sign-on, the established enterprise standard.</li>
      <li><strong>OpenID Connect (OIDC)</strong> for OAuth-2.0-based single sign-on, increasingly the default for newer identity providers.</li>
      <li><strong>SCIM 2.0</strong> for automated user provisioning, deprovisioning, and group membership sync from your IdP into eSign Launchpad.</li>
    </ul>

    <p>
      SAML and OIDC are alternatives to each other, pick one. SCIM stacks on top of either one
      to automate the user lifecycle so you do not have to add or remove eSign Launchpad accounts
      manually.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="saml">SAML 2.0 single sign-on</h2>
    <p>
      SAML (Security Assertion Markup Language) is the older of the two SSO protocols and the
      one most enterprise IdPs default to. eSign Launchpad acts as a SAML Service Provider; your
      IdP is the Identity Provider.
    </p>

    <h3>What you configure in eSign Launchpad</h3>
    <ul>
      <li>Identity Provider entity ID</li>
      <li>Single Sign-On URL (where we redirect users to authenticate)</li>
      <li>X.509 signing certificate (we pin this; rotation is explicit)</li>
      <li>Attribute mapping (which IdP claim becomes the user's email, name, role)</li>
      <li>Optional: group claim mapping for role assignment</li>
    </ul>

    <h3>What you configure in your IdP</h3>
    <ul>
      <li>Service Provider entity ID (we provide it on the SSO settings page)</li>
      <li>Assertion Consumer Service URL (we provide it)</li>
      <li>NameID format (typically <code>emailAddress</code>)</li>
      <li>Optional: groups attribute, transmitted as a multi-valued claim</li>
    </ul>

    <p>
      Once both sides are configured, users at your organization click "Log in with SSO" on the
      eSign Launchpad sign-in page, get redirected to your IdP, authenticate there, and return
      to eSign Launchpad with a signed SAML assertion. We verify the assertion against the
      pinned signing certificate and create or update their session accordingly.
    </p>

    <h3>Just-in-time provisioning</h3>
    <p>
      With SAML alone (no SCIM), eSign Launchpad creates user accounts on first SSO login. This
      is called just-in-time (JIT) provisioning. The user lands in the approval queue for
      Tenant Admin review before they get any package or signing access.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="oidc">OpenID Connect single sign-on</h2>
    <p>
      OpenID Connect (OIDC) is the modern alternative to SAML, built on OAuth 2.0. If your IdP
      offers both, OIDC is usually the cleaner choice: simpler configuration, JSON instead of XML,
      better mobile and SPA support.
    </p>

    <h3>What you configure in eSign Launchpad</h3>
    <ul>
      <li>OIDC Issuer URL (your IdP's discovery document, typically ends in <code>/.well-known/openid-configuration</code>)</li>
      <li>Client ID and Client Secret (issued by your IdP when you register eSign Launchpad as an OIDC client)</li>
      <li>Scopes to request (defaults: <code>openid profile email</code>; add <code>groups</code> if your IdP exposes group membership that way)</li>
      <li>Claim mapping (which token claim becomes the user's email, name, role)</li>
    </ul>

    <h3>What you configure in your IdP</h3>
    <ul>
      <li>Client type: Web (we use the authorization code flow with PKCE)</li>
      <li>Redirect URI (we provide it on the SSO settings page)</li>
      <li>Allowed scopes (at minimum: openid, profile, email)</li>
      <li>Optional: groups claim if you plan to map IdP groups to eSign Launchpad roles</li>
    </ul>

    <p>
      Once configured, the OIDC sign-in flow looks identical to SAML from a user's point of view:
      they click "Log in with SSO", get redirected, authenticate at the IdP, return signed in.
      Behind the scenes we exchange the authorization code for an ID token and validate its
      signature against the IdP's public JWKS endpoint.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="scim">SCIM 2.0 user provisioning</h2>
    <p>
      SCIM (System for Cross-domain Identity Management) is the protocol that lets your IdP
      push user lifecycle events to eSign Launchpad: create, update, deactivate, group-membership
      change. With SCIM in place, adding someone to the eSign Launchpad-bound IdP group
      automatically creates their account; removing them deactivates it.
    </p>

    <h3>What SCIM does for you</h3>
    <ul>
      <li><strong>Create</strong>: when a user is added to the eSign Launchpad SCIM-bound group in your IdP, eSign Launchpad creates their account and routes them through the approval queue.</li>
      <li><strong>Update</strong>: when their attributes change in the IdP (name, email, title), the changes flow through to eSign Launchpad on the next SCIM sync.</li>
      <li><strong>Deactivate</strong>: when they leave the SCIM-bound group (typically because they left the company), eSign Launchpad deactivates their account immediately, revoking signing access.</li>
      <li><strong>Group membership</strong>: SCIM groups push membership lists to eSign Launchpad, enabling group-to-role mapping.</li>
    </ul>

    <h3>What you configure</h3>
    <ul>
      <li>SCIM endpoint URL and Bearer token (we provide both on the SSO settings page)</li>
      <li>In your IdP's SCIM provisioning settings: enable Create / Update / Deactivate operations</li>
      <li>Attribute mapping (IdP attributes to SCIM standard attributes)</li>
      <li>Group filter (limit SCIM sync to specific groups, typically the eSign Launchpad-bound one)</li>
    </ul>

    <h3>Approval queue</h3>
    <p>
      Even with SCIM enabled, every newly-provisioned user lands in the eSign Launchpad approval
      queue for Tenant Admin review. This is intentional: SCIM creates the account record but
      does not grant signing access until a human approves and assigns a role. It prevents an IdP
      misconfiguration or compromise from automatically granting platform access.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="group-role-mapping">Group-to-role mapping</h2>
    <p>
      Both SAML and OIDC can carry group claims, and SCIM can sync group memberships directly.
      Either way, you can map IdP group membership to eSign Launchpad roles so the right
      permissions land automatically.
    </p>

    <p>
      Mapping happens on the eSign Launchpad SSO settings page: enter the IdP group name (the exact
      string the IdP sends in the group claim or SCIM group member list), and pick the eSign
      Launchpad role to grant. Common patterns:
    </p>

    <ul>
      <li>Map IdP group <code>esign-tenant-admins</code> to the eSign Launchpad <strong>Tenant Admin</strong> role.</li>
      <li>Map IdP group <code>esign-billing-admins</code> to the <strong>Billing Admin</strong> role.</li>
      <li>Map IdP group <code>esign-managers</code> to the <strong>Manager</strong> role.</li>
      <li>Map IdP group <code>esign-users</code> to the <strong>Supervisor</strong> role (the default end-user role).</li>
    </ul>

    <p>
      Users in multiple mapped groups receive the union of permissions. Users in no mapped
      groups land in the approval queue for manual role assignment.
    </p>

    <q-separator class="q-my-lg" />

    <h2 id="setup-checklist">Setup checklist</h2>
    <p>
      Whether you go with SAML or OIDC, the end-to-end setup is roughly the same:
    </p>

    <ol>
      <li>Decide which IdP and which SSO protocol (SAML or OIDC) you want to use.</li>
      <li>In eSign Launchpad: open Settings, then SSO. Pick your protocol; copy the SP entity ID and ACS URL (SAML) or redirect URI (OIDC) we display.</li>
      <li>In your IdP: register eSign Launchpad as a new application using the URLs we provided. Configure attribute mapping and optional group claims.</li>
      <li>Back in eSign Launchpad: paste in the IdP entity ID, signing certificate (SAML) or issuer URL plus client credentials (OIDC). Save.</li>
      <li>Test sign-in with an account that should have access. Verify the user lands in the approval queue with the right pending role.</li>
      <li>Optionally: enable SCIM provisioning on the same SSO settings page. Generate the SCIM bearer token, paste it into your IdP's SCIM provisioning configuration. Enable Create / Update / Deactivate.</li>
      <li>Optionally: configure group-to-role mappings so the approval queue can pre-suggest the right role.</li>
    </ol>

    <q-separator class="q-my-lg" />

    <h2 id="security-posture">Security posture</h2>
    <ul>
      <li><strong>Signing certificate pinning</strong>: SAML signing certificates are pinned per tenant. Rotation is explicit (you upload the new cert), preventing silent IdP-side changes from breaking signature validation.</li>
      <li><strong>Approval queue gate</strong>: every newly-provisioned user (JIT or SCIM) is gated for Tenant Admin approval before they can act on packages.</li>
      <li><strong>Role-removal protection</strong>: Tenant Admin role cannot be removed via SCIM or SSO group-mapping. Privileged role grants always require explicit Tenant Admin approval inside eSign Launchpad.</li>
      <li><strong>Audit trail</strong>: every user-lifecycle event (provisioning, role change, deactivation) is logged in the tamper-evident audit chain alongside package activity.</li>
      <li><strong>Session lifecycle</strong>: SSO sessions inherit your IdP's session policies. Deactivating a user in your IdP immediately invalidates their eSign Launchpad session on next request.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2 id="plan-availability">Plan availability</h2>
    <p>
      SSO (SAML and OIDC) and SCIM provisioning are available on the Business and Enterprise
      tiers. See <a href="/Home/Pricing">pricing</a> for the full tier comparison, or
      <a href="/Home/SmallBusiness">the small business and enterprise overview</a>
      for the broader feature picture for teams of 50-1000+ users.
    </p>

  </div>
</template>

<script>
export default {
    name: 'GuideEnterpriseSso'
};
</script>

<style scoped>
.guide-enterprise-sso h2 {
    scroll-margin-top: 80px;
}
.guide-enterprise-sso h3 {
    margin-top: 1.5rem;
}
.guide-enterprise-sso code {
    background: #F1F5F9;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 0.92em;
}
</style>
